/**
 * quantity-selector.js — QuantitySelector custom element
 *
 * Accessible +/- quantity stepper with min/max validation.
 *
 * Usage:
 *   <quantity-selector min="1" max="99">
 *     <button type="button" data-action="decrease" aria-label="Decrease quantity">−</button>
 *     <input type="number" name="quantity" value="1" min="1" max="99" />
 *     <button type="button" data-action="increase" aria-label="Increase quantity">+</button>
 *   </quantity-selector>
 *
 * Dispatches:
 *   quantity:change — on value change (detail: {value})
 */

import { Component } from '@theme/component';

export class QuantitySelector extends Component {
  #min = 1;
  #max = 99;

  onConnect() {
    this.#min = parseInt(this.getAttribute('min') || '1', 10);
    this.#max = parseInt(this.getAttribute('max') || '99', 10);

    const input = this.querySelector('input[type="number"]');
    const decreaseBtn = this.querySelector('[data-action="decrease"]');
    const increaseBtn = this.querySelector('[data-action="increase"]');

    if (decreaseBtn) this.on(decreaseBtn, 'click', () => this.#step(-1));
    if (increaseBtn) this.on(increaseBtn, 'click', () => this.#step(1));
    if (input) {
      this.on(input, 'change', () => this.#clamp());
      this.on(input, 'input', () => this.#clamp());
    }
  }

  get value() {
    const input = this.querySelector('input[type="number"]');
    return parseInt(input?.value || '1', 10);
  }

  set value(v) {
    const input = this.querySelector('input[type="number"]');
    if (input) {
      input.value = Math.max(this.#min, Math.min(this.#max, v));
      // Dispatch native input + change events so external listeners
      // (addEventListener on the input) stay in sync with programmatic
      // updates. Without this, product-form.js would not re-render the
      // price when the user clicks +/−.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.emit('quantity:change', { value: this.value });
    }
  }

  #step(direction) {
    this.value = this.value + direction;
  }

  #clamp() {
    const input = this.querySelector('input[type="number"]');
    if (!input) return;
    const raw = parseInt(input.value, 10) || this.#min;
    const clamped = Math.max(this.#min, Math.min(this.#max, raw));
    if (raw !== clamped) {
      input.value = clamped;
    }
    this.emit('quantity:change', { value: clamped });
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}
